import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Radio, ArrowRight } from "lucide-react";
import { fetchIdeas } from "../lib/api";

// The 10 main topic libraries. Innovation + Fun Facts are deliberately excluded.
const TOPIC_LABEL = {
  technology: "TECH",
  business: "BIZ",
  science: "SCIENCE",
  "health-fitness": "FOOD_HEALTH",
  finance: "FINANCE",
  politics: "POLITICAL LANDSCAPE",
  sports: "SPORTS",
  entertainment: "ENTERTAINMENT",
  education: "EDUCATION",
  environment: "CLIMATE",
};

const TOPIC_ACCENT = {
  technology: "text-emerald-300",
  business: "text-amber-300",
  science: "text-purple-300",
  "health-fitness": "text-pink-300",
  finance: "text-sky-300",
  politics: "text-orange-300",
  sports: "text-lime-300",
  entertainment: "text-rose-300",
  education: "text-cyan-300",
  environment: "text-teal-300",
};

const ALLOWED = new Set(Object.keys(TOPIC_LABEL));
const ROTATE_MS = 5000;

export default function TopicTicker() {
  // Pull the latest active ideas. Pull a healthy buffer so we can pick the
  // newest item from each of the 10 topics individually.
  const { data: ideas = [] } = useQuery({
    queryKey: ["ticker-ideas"],
    queryFn: () => fetchIdeas({ status: "active", limit: 200 }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  // Build a rotation list: one freshest idea per topic, ordered by topic.
  const items = useMemo(() => {
    const byTopic = new Map();
    for (const it of ideas) {
      const slug = it.topic_slug;
      if (!ALLOWED.has(slug)) continue;
      if (!byTopic.has(slug)) byTopic.set(slug, it);
    }
    // Preserve the topic order defined above so rotation feels deliberate.
    const ordered = [];
    for (const slug of Object.keys(TOPIC_LABEL)) {
      const it = byTopic.get(slug);
      if (it) ordered.push(it);
    }
    return ordered;
  }, [ideas]);

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (items.length === 0) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % items.length);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [items.length]);

  // Don't render if we have nothing yet — keeps the bar from flashing empty.
  if (items.length === 0) return null;

  const cur = items[idx % items.length];
  const slug = cur.topic_slug;
  const accent = TOPIC_ACCENT[slug] || "text-zinc-300";
  const label = TOPIC_LABEL[slug] || slug.toUpperCase();

  return (
    <div
      data-testid="topic-ticker"
      className="relative border-b border-[#27272A] bg-[#0E0E0E] overflow-hidden"
    >
      {/* faint amber glow on the left edge to draw the eye */}
      <div
        aria-hidden
        className="absolute left-0 top-0 h-full w-32 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, rgba(245,158,11,0.10), transparent 100%)",
        }}
      />
      <Link
        to={`/topics/${slug}`}
        data-testid={`topic-ticker-link-${slug}`}
        className="relative flex items-center gap-3 px-4 h-8 font-mono text-[11px] hover:bg-[#141414] group"
      >
        <span className="flex items-center gap-1.5 text-amber-300 tracking-wider">
          <Radio size={11} className="animate-pulse" />
          <span className="font-extrabold uppercase">LIVE</span>
        </span>
        <span className="w-px h-3 bg-[#27272A]" />
        <span
          data-testid="topic-ticker-topic"
          className={`uppercase tracking-[0.2em] font-extrabold ${accent}`}
        >
          {label}
        </span>
        <span className="text-zinc-600">›</span>
        <span
          data-testid="topic-ticker-title"
          key={cur.id}
          className="text-zinc-200 truncate fade-in-up flex-1"
        >
          {cur.title}
        </span>
        <span className="hidden sm:flex items-center gap-1 text-zinc-500 group-hover:text-white uppercase tracking-wider text-[10px] shrink-0">
          OPEN_LIB <ArrowRight size={10} />
        </span>
        {/* dot indicator showing rotation progress */}
        <span className="hidden md:flex items-center gap-1 ml-2 shrink-0">
          {items.map((_, i) => (
            <span
              key={i}
              className={`inline-block w-1 h-1 rounded-full ${
                i === idx % items.length ? "bg-amber-400" : "bg-zinc-700"
              }`}
            />
          ))}
        </span>
      </Link>
    </div>
  );
}
